import I18n from 'I18n';
import styled from 'styled-components';
import { useCallback, useEffect, useState } from 'react';
import { useDataFetchingClient } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIWizard from 'UIComponents/dialog/UIWizard';
import UIWizardHeaderWithOverview from 'UIComponents/dialog/UIWizardHeaderWithOverview';
import UIWizardStep from 'UIComponents/dialog/UIWizardStep';
import PortalIdParser from 'PortalIdParser';
import { useLazyQuery } from 'data-fetching-client';
import WebhookEnrollmentStep from 'automation-ui-enrollment/components/eventEnrollment/steps/WebhookEnrollmentStep';
import WebhookConnectToHubSpotStep from 'automation-ui-enrollment/components/eventEnrollment/steps/WebhookConnectToHubspotStep';
import WebhookPropertyMappingStep from 'automation-ui-enrollment/components/eventEnrollment/steps/WebhookPropertyMappingStep';
import { INGEST_WEBHOOK_DEF } from 'automation-ui-enrollment/queries/WebhookQueries';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import UIModal from 'UIComponents/dialog/UIModal';
import WebhookNameEventStep from 'automation-ui-enrollment/components/eventEnrollment/steps/WebhookNameEventStep';
import WebhookCreationSuccessStep from 'automation-ui-enrollment/components/eventEnrollment/steps/WebhookCreationSuccessStep';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledWizardStep = styled(UIWizardStep).withConfig({
  displayName: "WebhookWizard__StyledWizardStep",
  componentId: "sc-1jyruw7-0"
})(["margin:0 auto;width:50%;"]);

// Used for tracking
const WebhookStepIndicesToEventTypes = {
  0: 'NAME',
  1: 'CONNECT',
  2: 'MAP',
  3: 'MATCH'
};
const WebhookWizard = ({
  objectTypeId,
  onClose
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [disableNext, setDisableNext] = useState(true);
  const [webhookData, setWebhookData] = useState({});
  const [triggerName, setTriggerName] = useState();
  const [callbackId, setCallbackId] = useState();
  const [propertyMappings, setPropertyMappings] = useState([]);
  const [eventProperties, setEventProperties] = useState([]);
  const [propertyValueDefinitions, setPropertyValueDefinitions] = useState([]);
  const [associationMode, setAssociationMode] = useState('USE_EXISTING_OBJECT');
  const [objectAssociation, setObjectAssociation] = useState();
  const [selectedObjectTypeId, setSelectedObjectTypeId] = useState(objectTypeId);
  const dataFetchingClient = useDataFetchingClient();
  const trackEvent = useTrackEvent();
  const [postWebhookDef, {
    called: webhookSaveCalled,
    loading: webhookSaveLoading,
    error: webhookSaveError
  }] = useLazyQuery(INGEST_WEBHOOK_DEF);

  // On unmount, clear the webhook initializer cache so we don't persist next time the modal is opened
  useEffect(() => {
    return () => {
      dataFetchingClient.cache.evict({
        fieldName: 'initializeWebhook'
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the webhookMappings or object type change, we can't keep the same property value definitions, so we reset them
  useEffect(() => {
    setPropertyValueDefinitions([]);
  }, [propertyMappings, setPropertyValueDefinitions, objectTypeId]);
  useEffect(() => {
    if (webhookSaveError) {
      setDisableNext(true);
    }
  }, [webhookSaveError]);
  const handlePolledDataReceived = useCallback((data, id) => {
    setCallbackId(id);
    setWebhookData(data.response);
    // Reset the values for future steps
    setPropertyMappings([]);
    setEventProperties([]);
    setObjectAssociation(undefined);
    setPropertyValueDefinitions([]);
  }, []);
  const onSaveWebhook = useCallback(() => {
    trackEvent(Trackables.WEBHOOK_TRIGGER_MODIFIED, {
      editType: 'CREATE_CONFIRM',
      objectTypeId: objectTypeId !== null && objectTypeId !== void 0 ? objectTypeId : selectedObjectTypeId
    });
    const webhookIngestionBody = {
      eventProperties,
      objectAssociation: objectAssociation,
      objectTypeId: selectedObjectTypeId,
      label: triggerName,
      callbackId: callbackId,
      portalId: PortalIdParser.get()
    };
    postWebhookDef({
      variables: {
        webhookIngestionBody
      }
    });
  }, [trackEvent, eventProperties, objectAssociation, triggerName, objectTypeId, selectedObjectTypeId, callbackId, postWebhookDef]);
  const allowReturnThroughWizard = !webhookSaveCalled || !!webhookSaveError;
  return /*#__PURE__*/_jsx(UIModal, {
    use: "fullscreen",
    children: /*#__PURE__*/_jsxs(UIWizard, {
      stepIndex: stepIndex,
      backable: allowReturnThroughWizard,
      cancellable: allowReturnThroughWizard,
      defaultHeading: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.webhookWizard.heading"
      }),
      headerComponent: UIWizardHeaderWithOverview,
      onReject: () => {
        trackEvent(Trackables.WEBHOOK_WIZARD_CLOSED, {
          objectTypeId: objectTypeId !== null && objectTypeId !== void 0 ? objectTypeId : selectedObjectTypeId
        });
        onClose();
      },
      onConfirm: onClose,
      onStepIndexChange: ({
        target: {
          value: nextIndex
        }
      }) => {
        if (nextIndex > stepIndex) {
          if (nextIndex in WebhookStepIndicesToEventTypes) {
            trackEvent(Trackables.WEBHOOK_WIZARD_STEP_CHANGE, {
              step: WebhookStepIndicesToEventTypes[stepIndex],
              objectTypeId: objectTypeId !== null && objectTypeId !== void 0 ? objectTypeId : selectedObjectTypeId
            });
          }
        } else {
          // Enable next button if we are traveling backwards through the wizard
          setDisableNext(false);
        }
        setStepIndex(nextIndex);
      },
      disablePrimaryButton: disableNext,
      defaultDoneLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.webhookWizard.creationFinalization.success.continueToSetup"
      }),
      children: [/*#__PURE__*/_jsx(StyledWizardStep, {
        name: I18n.text('automation-ui-enrollment.webhookWizard.nameEvent.header'),
        children: /*#__PURE__*/_jsx(WebhookNameEventStep, {
          setTriggerName: setTriggerName,
          triggerName: triggerName,
          setDisableNext: setDisableNext
        })
      }), /*#__PURE__*/_jsx(StyledWizardStep, {
        name: I18n.text('automation-ui-enrollment.webhookWizard.connectingApp.header'),
        children: /*#__PURE__*/_jsx(WebhookConnectToHubSpotStep, {
          handleDataReceived: handlePolledDataReceived,
          setDisableNext: setDisableNext,
          hasExistingWebhookData: Object.keys(webhookData).length !== 0,
          setWebhookData: setWebhookData
        })
      }), /*#__PURE__*/_jsx(StyledWizardStep, {
        name: I18n.text(`automation-ui-enrollment.webhookWizard.setupEvent.header`),
        children: /*#__PURE__*/_jsx(WebhookPropertyMappingStep, {
          setTriggerName: setTriggerName,
          triggerName: triggerName,
          setDisableNext: setDisableNext,
          webhookData: webhookData,
          propertyMappings: propertyMappings,
          setPropertyMappings: setPropertyMappings,
          setEventProperties: setEventProperties
        })
      }), /*#__PURE__*/_jsx(StyledWizardStep, {
        name: I18n.text('automation-ui-enrollment.webhookWizard.associatingObject.header'),
        children: /*#__PURE__*/_jsx(WebhookEnrollmentStep, {
          allowObjectTypeSelection: !objectTypeId,
          objectTypeId: selectedObjectTypeId,
          setObjectTypeId: setSelectedObjectTypeId,
          setObjectAssociation: setObjectAssociation,
          propertyMappings: propertyMappings,
          setDisableSubmit: setDisableNext,
          setPropertyValueDefinitions: setPropertyValueDefinitions,
          propertyValueDefinitions: propertyValueDefinitions,
          associationMode: associationMode,
          setAssociationMode: setAssociationMode
        })
      }), /*#__PURE__*/_jsx(StyledWizardStep, {
        name: I18n.text('automation-ui-enrollment.webhookWizard.associatingObject.header'),
        children: /*#__PURE__*/_jsx(WebhookCreationSuccessStep, {
          onSaveWebhook: onSaveWebhook,
          webhookSaveCalled: webhookSaveCalled,
          webhookSaveLoading: webhookSaveLoading,
          webhookSaveError: webhookSaveError
        })
      })]
    })
  });
};
export default WebhookWizard;